import { waterTankThresholdColorScale } from '@weenat/client/dist/core/dss/irrigation'
import { useConvertedValue } from '@weenat/client/dist/hooks'

import { useClient, useQuery } from '@weenat/client'
import {
  CheckInWindowMinutes,
  DangerThresholdMinutes,
  UpdateWarningThresholds
} from '@weenat/client/dist/enums/Devices'
import { LatestData } from '@weenat/client/dist/resources/latest'
import { Plot } from '@weenat/client/dist/resources/plots'
import {
  WaterTankThresholdType,
  waterTankThresholdTypes
} from '@weenat/client/dist/resources/thresholds.types'
import { useIntl } from '@weenat/wintl'
import { PlotListItemsVariants } from 'app/src/dashboard/components/DashboardMap/plotListItem/PlotListItemMetricSummary'
import { PREVENT_DRAG_CLASSNAME } from 'app/src/dashboard/components/plots/dashboards/constants'
import useDisclosure from 'app/src/hooks/useDisclosure'
import { useToggleFeature } from 'app/state'
import logEvent from 'app/utils/analytics'
import { clamp, isEmpty, isNil, isNumber, max } from 'lodash-es'
import moment from 'moment-timezone'
import { transparentize } from 'polished'
import { memo } from 'react'
import { styled, useTheme } from 'styled-components'
import ChartLegendItem from '../charts/ChartLegendItem'
import DelimitedFlex from '../DelimitedFlex'
import EmptyStateWithRipple from '../empty-state/EmptyStateWithRipple'
import Icons from '../Icons'
import Modal from '../Modal'
import Text from '../Text'
import UpdateInformation from '../UpdateInformation'
import WeenatPressable from '../WeenatPressable'
import WeedgetContextMenu from './ContextMenu'
import { IrrigationRestrictionWeedget } from './IrrigationRestrictionWeedget'
import WeedgetCard from './WeedgetCard'
import WeedgetHeader from './WeedgetHeader'
import WeedgetLoading from './WeedgetLoading'

const CHART_LEGEND_STYLE = { gap: 12 }

const HORIZON_COLUMN_WIDTH = 64
const VALUE_COLUMN_WIDTH = 48

interface ReservoirProfileWeedgetProps {
  plot: Plot
  onDelete?: () => void
  isRestricted?: boolean
}

const ProfileBarContainer = styled(Flex)<{
  $isFirst: boolean
  $isLast: boolean
  $variant?: PlotListItemsVariants
}>`
  flex: 1;
  position: relative;
  justify-content: center;
  align-items: center;
  border-width: ${(p) =>
    p.$variant === 'list'
      ? '0px'
      : p.$isFirst && p.$isLast
        ? '1px'
        : p.$isFirst
          ? '1px 1px 0px 1px'
          : p.$isLast
            ? '0px 1px 1px 1px'
            : '0px 1px'};
  border-radius: ${(p) =>
    p.$isFirst && p.$isLast
      ? `${p.theme.radiuses.sm}px`
      : p.$isFirst
        ? `${p.theme.radiuses.sm}px ${p.theme.radiuses.sm}px 0 0`
        : p.$isLast
          ? `0 0 ${p.theme.radiuses.sm}px ${p.theme.radiuses.sm}px`
          : '0px'};
  border-style: solid;
  border-color: ${(p) => p.theme.colors.grayscale[200]};
  height: 100%;
  overflow: hidden;
  background-color: transparent;

  &:before {
    content: ${(p) => (p.$variant === 'list' ? 'none' : "''")};
    position: absolute;
    width: 150%;
    height: 1000%;
    top: -400%;
    left: -25%;
    background: ${(p) => p.theme.colors.grayscale.white};
    background-image: ${(p) =>
      `radial-gradient(${p.theme.colors.grayscale[100]} 2px, transparent 0)`};
    background-size: 10px 10px;
    transform: rotate(45deg);
  }

  & > * {
    z-index: 1;
  }
`

const ProfileCursor = styled(Flex)<{ $percentageValue: number; $variant?: PlotListItemsVariants }>`
  position: absolute;
  height: ${(p) => (p.$variant === 'tooltip' ? '24px' : '16px')};
  width: ${(p) => (p.$variant === 'tooltip' ? '8px' : '4px')};
  background-color: ${(p) => p.theme.colors.grayscale.white};
  border-color: ${(p) => p.theme.colors.grayscale.black};
  border-style: solid;
  border-width: 2px;
  border-radius: ${(p) => p.theme.radiuses.sm}px;
  top: 50%;
  transform: translate(-50%, -50%);
  left: ${(p) => clamp(0, p.$percentageValue, 100)}%;
  -webkit-filter: ${(p) => `drop-shadow(
    ${p.theme.shadows.sm.shadowOffset.width}px 
    ${p.theme.shadows.sm.shadowOffset.height}px 
    ${p.theme.shadows.sm.shadowRadius}px 
    ${transparentize(1 - p.theme.shadows.sm.shadowOpacity, p.theme.shadows.sm.shadowColor)})`};
  filter: ${(p) => `drop-shadow(
    ${p.theme.shadows.sm.shadowOffset.width}px 
    ${p.theme.shadows.sm.shadowOffset.height}px 
    ${p.theme.shadows.sm.shadowRadius}px 
    ${transparentize(1 - p.theme.shadows.sm.shadowOpacity, p.theme.shadows.sm.shadowColor)})`};
`

const WaterTankLegend: FC<{ waterTankKey: WaterTankThresholdType }> = ({ waterTankKey }) => {
  const { t } = useIntl()
  const thresholdColor = waterTankThresholdColorScale(waterTankKey)
  return (
    <ChartLegendItem
      color={thresholdColor}
      labelSize='sm'
      label={t(`water_tank_profile.legend.${waterTankKey}`)}
      itemSize={12}
      style={CHART_LEGEND_STYLE}
    />
  )
}

const ProfileLegend: FC<{ maxValue: number | null; ticksNumber: number }> = ({
  maxValue,
  ticksNumber
}) => {
  const { t } = useIntl()

  const { getUnit } = useConvertedValue()
  const unit = getUnit('WATER_TANK_LEVEL')

  const ticks = !isNil(maxValue)
    ? Array.from({ length: ticksNumber }, (_, i) => (i * maxValue) / (ticksNumber - 1))
    : []

  return (
    <Flex
      $width={'100%'}
      $backgroundColor={'grayscale.white'}
      style={{ bottom: 0 }}
      $px='md'
      $mt='md'
    >
      <Flex $width={HORIZON_COLUMN_WIDTH} />
      <Flex
        $position='relative'
        $flex={1}
        $height={1}
        $borderWidth={0}
        style={{ borderTopWidth: 1, borderRightWidth: 1 }}
        $borderColor={'grayscale.black'}
      >
        {ticks.map((value, i, arr) => {
          return (
            <Flex
              key={value}
              $position='absolute'
              $flexDirection='column'
              style={{ left: `${(100 * i) / (arr.length - 1)}%` }}
            >
              <Flex $height={6} $width={1} $backgroundColor={'grayscale.black'} />
              {(i === 0 || i === arr.length - 1) && (
                <Text $fontSize='sm' $textAlign='center' style={{ transform: 'translateX(-50%)' }}>
                  {Math.floor(value)}
                </Text>
              )}
            </Flex>
          )
        })}
      </Flex>
      <Flex $width={VALUE_COLUMN_WIDTH} $justifyContent='flex-end'>
        <Text $fontWeight='medium'>{t(`units.${unit}_abbr`)}</Text>
      </Flex>
    </Flex>
  )
}

export const ProfileBar: FC<{
  thresholds: { [key in WaterTankThresholdType]: number }
  value?: number
  maxCapacity: number | null
  isFirst?: boolean
  isLast?: boolean
  variant?: PlotListItemsVariants
}> = memo(
  ({ thresholds, value, maxCapacity, isFirst = false, isLast = false, variant = 'tooltip' }) => {
    const { radiuses } = useTheme()

    const toPercentageOfMax = (_value?: number) =>
      !isNil(_value) && !isNil(maxCapacity)
        ? (Math.min(_value, maxCapacity) * 100) / maxCapacity
        : 0

    const percentageValue = toPercentageOfMax(value)

    return (
      <ProfileBarContainer $isFirst={isFirst} $isLast={isLast} $variant={variant}>
        <Flex $mx='md' $flex={1} $position='relative'>
          {Object.entries(thresholds)
            .filter(([, threhsholdValue]) => threhsholdValue > 0)
            .map(([thresholdName, thresholdValue], i, arr) => {
              const thresholdColor = waterTankThresholdColorScale(
                thresholdName as WaterTankThresholdType
              )

              const borderRadius =
                arr.length === 1
                  ? `${radiuses.xs}px`
                  : i === 0
                    ? `${radiuses.xs}px 0 0 ${radiuses.xs}px`
                    : i === arr.length - 1
                      ? `0 ${radiuses.xs}px ${radiuses.xs}px 0`
                      : '0'

              return (
                <Flex
                  key={thresholdName}
                  $backgroundColor={thresholdColor}
                  $height={12}
                  $width={`${toPercentageOfMax(thresholdValue)}%`}
                  $borderRadius={borderRadius}
                />
              )
            })}
          {!isNil(value) && <ProfileCursor $percentageValue={percentageValue} $variant={variant} />}
        </Flex>
      </ProfileBarContainer>
    )
  }
)

const WaterTankProfile: FC<{
  data: LatestData
}> = ({ data }) => {
  const { convertValue } = useConvertedValue()

  const horizons = Object.keys({
    ...data.SURVIVAL_WATER_TANK,
    ...data.READILY_WATER_CONTENT,
    ...data.WATER_TANK_LEVEL,
    ...data.WATER_TANK_MAX
  })

  const horizonsValue = [
    ...Object.values(data?.SURVIVAL_WATER_TANK || {}),
    ...Object.values(data?.READILY_WATER_CONTENT || {}),
    ...Object.values(data?.WATER_TANK_LEVEL || {}),
    ...Object.values(data?.WATER_TANK_MAX || {})
  ]

  const maxCapacity = max(horizonsValue)
  /* Display a 5% saturation above WATER_TANK_MAX / the WATER_TANK_LEVEL */
  const correctedMaxCapacity = !isNil(maxCapacity) ? Math.round(1.05 * maxCapacity) : null

  const horizonThresholds: Array<{ [key in WaterTankThresholdType]: number }> = horizons.map(
    (horizon) => {
      const horizonSurvivalWaterTank = data.SURVIVAL_WATER_TANK?.[horizon] ?? 0
      const horizonReadilyWaterContent = data.READILY_WATER_CONTENT?.[horizon] ?? 0
      const horizonWaterTankMax = data.WATER_TANK_MAX?.[horizon] ?? 0

      return {
        survival_water_tank: horizonSurvivalWaterTank,
        rfu: horizonReadilyWaterContent,
        saturation: (correctedMaxCapacity ?? 0) - horizonWaterTankMax
      }
    }
  )

  return (
    <Flex $flex={1} $overflow='auto' $p='md'>
      <Flex $flex={1} $flexDirection='column' $justifyContent='center' $minHeight={'max-content'}>
        {horizons.map((horizon, i, arr) => {
          return (
            <Flex
              key={horizon}
              $alignItems='center'
              $borderColor={'grayscale.500'}
              $borderWidth={0}
              $justifyContent='center'
              style={{ borderTopWidth: i === 0 ? 0 : '1px', borderStyle: 'solid' }}
              $height={32}
              $minHeight={32}
            >
              <Flex
                $width={HORIZON_COLUMN_WIDTH}
                $justifyContent='flex-start'
                $alignItems='center'
                $px='md'
              >
                <Icons.ArrowDown $size='md' />
                <Text $fontSize='rg' $fontWeight='medium'>
                  {!isNaN(parseInt(horizon)) ? horizon : '—'}
                </Text>
              </Flex>

              <ProfileBar
                thresholds={horizonThresholds[i]}
                maxCapacity={correctedMaxCapacity}
                value={
                  isNumber(data?.WATER_TANK_LEVEL?.[horizon])
                    ? clamp(0, data.WATER_TANK_LEVEL?.[horizon], 100)
                    : undefined
                }
                isFirst={i === 0}
                isLast={i === arr.length - 1}
              />
              <Flex
                $width={VALUE_COLUMN_WIDTH}
                $justifyContent='flex-end'
                $alignItems='center'
                $px='md'
              >
                <Text $fontSize='rg' $fontWeight='medium' $textAlign='end'>
                  {convertValue({
                    metric: 'WATER_TANK_LEVEL',
                    displayUnit: false,
                    maximumFractionDigits: 0,
                    value: isNumber(data?.WATER_TANK_LEVEL?.[horizon])
                      ? clamp(0, data?.WATER_TANK_LEVEL?.[horizon], 100)
                      : null,
                    showEmptyState: true
                  })}
                </Text>
              </Flex>
            </Flex>
          )
        })}
        <ProfileLegend maxValue={correctedMaxCapacity} ticksNumber={6} />
      </Flex>
    </Flex>
  )
}

const ReservoirProfileWeedget: FC<ReservoirProfileWeedgetProps> = ({
  plot,
  onDelete,
  isRestricted
}) => {
  const { t } = useIntl()
  const [{ marker_redesign }] = useToggleFeature()

  const { open, close, isOpen } = useDisclosure()

  const handleOpenLegend = () => {
    logEvent('open_md_watertank_profile')
    open()
  }

  const client = useClient()

  const { data: latestPlotData, isLoading } = useQuery(client.latest.get(plot.id, 'plots'))

  const WATER_TANK_devices = (latestPlotData?.devices ?? []).filter(
    (device) =>
      device.availableMeasures.includes('HPOT') ||
      device.availableMeasures.includes('U_CAPA') ||
      device.availableMeasures.includes('SWC')
  )

  const WATER_TANK_data = {
    WATER_TANK_LEVEL: latestPlotData?.data?.WATER_TANK_LEVEL,
    SURVIVAL_WATER_TANK: latestPlotData?.data?.SURVIVAL_WATER_TANK,
    READILY_WATER_CONTENT: latestPlotData?.data?.READILY_WATER_CONTENT,
    WATER_TANK_MAX: latestPlotData?.data?.WATER_TANK_MAX
  }

  const isDataEmpty =
    isNil(WATER_TANK_data) ||
    Object.values(WATER_TANK_data).every((value) => isNil(value) || isEmpty(value))

  const closestTimestamp = Math.max(
    ...WATER_TANK_devices.map((d) => d.datetime?.getTime()).filter((d) => !isNil(d))
  )
  const WATER_TANK_timestamp = moment(closestTimestamp).unix()
  const WATER_TANK_endName =
    isNil(WATER_TANK_devices) || isEmpty(WATER_TANK_devices)
      ? '—'
      : WATER_TANK_devices.length > 1
        ? t('sensor_profile.tensiometer_kit')
        : WATER_TANK_devices[0]?.endUserName.join(' ')

  return (
    <>
      <WeedgetCard $position='relative' $flex={1} $overflow='hidden'>
        {/*  HEADER */}
        <WeedgetHeader>
          <Flex $flexDirection='column' $flex={1} $minWidth={0}>
            <Text $fontWeight='bold' $ellipsis>
              {t('water_tank_profile.widget_title')}
            </Text>
          </Flex>
          {!isLoading && !isDataEmpty && (
            <Icons.InfoCircle
              onPress={handleOpenLegend}
              className={PREVENT_DRAG_CLASSNAME}
              $backgroundColor='grayscale.100'
              $color='grayscale.black'
              $p='sm'
              $size='lg'
            />
          )}
          <WeedgetContextMenu onDelete={onDelete} />
        </WeedgetHeader>

        {/* CONTENT */}
        {isLoading ? (
          <WeedgetLoading />
        ) : isRestricted ? (
          <IrrigationRestrictionWeedget />
        ) : !isDataEmpty ? (
          <>
            <WaterTankProfile data={WATER_TANK_data} />
            {/* FOOTER */}
            <DelimitedFlex
              $alignItems='center'
              $isDelimitedOnTop
              $p='md'
              $justifyContent='center'
              $gap='sm'
              $borderRadius={0}
            >
              <UpdateInformation
                name={WATER_TANK_endName}
                timestamp={WATER_TANK_timestamp}
                warningThreshold={
                  marker_redesign
                    ? CheckInWindowMinutes.irrigation
                    : UpdateWarningThresholds.irrigation
                }
                dangerThreshold={DangerThresholdMinutes.irrigation}
                timezone={plot.timezone}
              />
            </DelimitedFlex>
          </>
        ) : (
          <>
            <Flex
              $flex={1}
              $flexDirection='column'
              $alignItems='center'
              $justifyContent='center'
              $gap='md'
            >
              <EmptyStateWithRipple
                Icon={Icons.Circle}
                title={t('water_tank_profile.impossible_to_display_water_tank_profile')}
                description={t('water_tank_profile.no_data')}
              />
            </Flex>
            <WeenatPressable onPress={onDelete} targetFirstChild className={PREVENT_DRAG_CLASSNAME}>
              <DelimitedFlex
                $alignItems='center'
                $isDelimitedOnTop
                $p='md'
                $justifyContent='center'
                $gap='sm'
              >
                <Icons.Bin $color={'feedback.error.500'} $size='md' />
                <Text $color={'feedback.error.500'}>{t('weedget.actions.delete')}</Text>
              </DelimitedFlex>
            </WeenatPressable>
          </>
        )}
      </WeedgetCard>
      {/* MODAL */}
      {isOpen && (
        <Modal
          isOpen={isOpen}
          close={close}
          hasCloseIcon
          title={t('water_tank_profile.legend.title')}
        >
          <Flex $flexDirection='column' $gap='lg'>
            <Flex
              $alignItems='center'
              $gap={'sm'}
              style={CHART_LEGEND_STYLE}
              $width={'max-content'}
            >
              <Box
                $width={8}
                $height={16}
                $mx='xs'
                $backgroundColor={'grayscale.white'}
                $borderColor={'grayscale.black'}
                $borderWidth={2}
                $borderRadius='md'
              />
              <Text $fontSize={'sm'}>{t('water_tank_profile.legend.water_tank_level')}</Text>
            </Flex>
            {waterTankThresholdTypes.map((key) => (
              <WaterTankLegend waterTankKey={key} key={key} />
            ))}
          </Flex>
        </Modal>
      )}
    </>
  )
}

export default ReservoirProfileWeedget
